import {
    Component,
    EventEmitter,
    Input,
    Output,
    SimpleChanges,
    OnInit,
    OnChanges
} from '@angular/core';
import { Job, JobSearchQuery, JobService } from '@intranet/modules/jobs';
import { Client } from '@intranet/modules/clients/client.types';

@Component({
    selector: 'job-value-link-job',
    templateUrl: './job-value-link-job.component.html',
    styleUrls: ['./job-value-link-job.component.scss']
})
export class JobValueLinkJobComponent implements OnInit, OnChanges {
    @Input() showModal: boolean = false;
    @Input() clients: Client[] = [];
    @Input() selectedClient: number | null = null;
    @Output() closeModal = new EventEmitter<void>();
    @Output() linkJob = new EventEmitter<Job>();

    jobs: Job[] = [];
    showArchived: boolean = false;
    loading: boolean = false;
    private requestCounter = 0;

    private jobCache: { [key: string]: Job[] } = {}; // 'live-123' or 'archived-123'

    filterQuery: JobSearchQuery = {
        page: 1,
        sortBy: 'type',
        sortDir: 'desc',
        clients: []
    };

    constructor(private jobService: JobService) {}

    ngOnInit(): void {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedClient'] && this.showModal) {
            const newClientId = this.selectedClient ?? null;

            // If client has changed, update the filter and force a refresh
            if (newClientId !== this.filterQuery.clientId) {
                this.filterQuery.clientId = newClientId;
                this.jobs = []; // clear stale jobs to avoid flicker
                this.resetToLiveTab(true); // force refresh
            }
        }

        if (changes['showModal'] && changes['showModal'].currentValue === true) {
            // Set the client filter to the job's client when modal opens
            this.filterQuery.clientId = this.selectedClient ?? null;
            this.resetToLiveTab();
        }
    }


    private resetToLiveTab(forceRefresh: boolean = false): void {
        this.showArchived = false;

        const userId = this.filterQuery.userId || 'all';
        const cacheKey = `live-${this.filterQuery.clientId}-${userId}`;
        if (!forceRefresh && this.jobCache[cacheKey]) {
            this.jobs = this.jobCache[cacheKey];
            return;
        }

        this.loadJobsFromCacheOrAPI(forceRefresh);
    }

    close(): void {
        this.closeModal.emit();
    }

    onLinkJob(job: Job): void {
        this.linkJob.emit(job);
        this.close();
    }

    onFilter(query: JobSearchQuery) {
      const normalize = (val: string | undefined) => (val || '').trim().toLowerCase();
        const isSameQuery =
            query.clientId === this.filterQuery.clientId &&
            query.userId === this.filterQuery.userId &&
            query.sortBy === this.filterQuery.sortBy &&
            query.sortDir === this.filterQuery.sortDir &&
            normalize(query.searchTerm) === normalize(this.filterQuery.searchTerm);

        this.filterQuery = query;

        this.loadJobsFromCacheOrAPI(!isSameQuery); // only force refresh if filters changed
    }

    onSort(sortKey: string) {
        const isSame = this.filterQuery.sortBy === sortKey;
        this.filterQuery.sortBy = sortKey;
        this.filterQuery.sortDir = isSame
            ? (this.filterQuery.sortDir === 'asc' ? 'desc' : 'asc')
            : 'asc';

        this.loadJobsFromCacheOrAPI(true);
    }

    onStatusChange(status: 'live' | 'archived'): void {
        this.showArchived = status === 'archived';
        this.loadJobsFromCacheOrAPI();
    }

    private loadJobsFromCacheOrAPI(forceRefresh: boolean = false): void {
        const clientId = this.filterQuery.clientId;
        const userId = this.filterQuery.userId || 'all';
        const cacheKey = `${this.showArchived ? 'archived' : 'live'}-${clientId}-${userId}`;

        if (!forceRefresh && this.jobCache[cacheKey]) {
            this.jobs = this.jobCache[cacheKey];
            return;
        }

        this.loading = true;
        const currentRequestId = ++this.requestCounter;

        const clientFilter = clientId
            ? [clientId]
            : this.clients.map((c) => c.clientId);

        this.jobService
          .getJobs({
            ...this.filterQuery,
            archived: this.showArchived,
            clients: clientFilter
          })
          .subscribe({
              next: (jobs) => {
                  if (currentRequestId === this.requestCounter) {
                      this.jobs = jobs;
                      this.jobCache[cacheKey] = jobs; // cache results
                      this.loading = false;
                  }
              },
              error: (err) => {
                  if (currentRequestId === this.requestCounter) {
                      console.error('❌ Failed to fetch jobs:', err);
                      this.loading = false;
                  }
              }
          });
    }
}
